import { useState } from 'react'
import { useIsOrganisationAdmin } from '@hooks'
import { Download, Edit, Storage, ContentCopy, Delete } from '@mui/icons-material'
import Filter2Icon from '@mui/icons-material/Filter2'
import { CircularProgress, IconButton, Stack, Tooltip, Divider } from '@mui/material'
import { AlertDialog, IconButtonProgress } from '@ui/feedback'

const EditGlobalAssemblyButton = ({ onEdit, dataCy }: { onEdit: () => void; dataCy: string }) => {
  return (
    <AlertDialog
      title="Aufbauten verändern?"
      text={
        'Die Änderung am Aufbau wird nicht in bestehende Projekte übernommen. Die Aufbauten müssen <b>jeweils</b> in den entsprechenden Projekten im Aufbauten Manager geändert werden.'
      }
      onConfirm={onEdit}
    >
      <Tooltip title="Bearbeiten" enterDelay={500}>
        <IconButton data-cy={`${dataCy}-btn-edit`}>
          <Edit fontSize="small" />
        </IconButton>
      </Tooltip>
    </AlertDialog>
  )
}

const EditProjectAssemblyButton = ({ onEdit, dataCy }: { onEdit: () => void; dataCy: string }) => {
  return (
    <AlertDialog
      title="Aufbauten verändern?"
      text="Änderungen an diesem Aufbau werden nur innerhalb des Projektes angewandt und wirken sich nicht auf andere Projekte aus."
      onConfirm={onEdit}
    >
      <Tooltip title="Bearbeiten" enterDelay={500}>
        <IconButton data-cy={`${dataCy}-btn-edit`}>
          <Edit fontSize="small" />
        </IconButton>
      </Tooltip>
    </AlertDialog>
  )
}

const DownloadAssemblyButton = ({ onClick }: { onClick: () => void }) => {
  const [isDownloading, setIsDownloading] = useState(false)

  return (
    <IconButtonProgress
      isLoading={isDownloading}
      onClick={() => {
        setIsDownloading(true)
        onClick()
        setIsDownloading(false)
      }}
      tooltip="Aufbau Datenblatt herunterladen"
      tooltipProps={{ enterDelay: 500 }}
    >
      <Download fontSize="small" />
    </IconButtonProgress>
  )
}

const StoreAssemblyButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <Tooltip title="Im globalen Aufbauten Manager speichern">
      <IconButton onClick={onClick}>
        <Storage fontSize="small" />
      </IconButton>
    </Tooltip>
  )
}

const DuplicateAssemblyButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <Tooltip title="Duplizieren" enterDelay={500}>
      <IconButton onClick={onClick}>
        <Filter2Icon fontSize="small" />
      </IconButton>
    </Tooltip>
  )
}

const CopyAssemblyButton = ({ onClick, dataCy }: { onClick: () => void; dataCy: string }) => {
  return (
    <Tooltip title="In Zwischenablage kopieren" enterDelay={500}>
      <IconButton onClick={onClick} data-cy={`${dataCy}-copy`}>
        <ContentCopy fontSize="small" />
      </IconButton>
    </Tooltip>
  )
}

const DeleteAssemblyButton = ({
  onClick,
  disabled,
  loading,
}: {
  onClick: () => void
  disabled?: boolean
  loading?: boolean
}) => {
  return (
    <Tooltip title="Löschen" enterDelay={500}>
      {loading ? (
        <CircularProgress size={15} />
      ) : (
        <IconButton onClick={onClick} disabled={disabled}>
          <Delete fontSize="small" />
        </IconButton>
      )}
    </Tooltip>
  )
}

const ProjectAssemblyActions = ({
  onEdit,
  onDelete,
  onDownload,
  onStore,
  onDuplicate,
  onCopy,
  dataCy,
  deleteDisabled,
  deleteLoading,
}: {
  onEdit: () => void
  onDelete: () => void
  onDownload: () => void
  onStore: () => void
  onDuplicate: () => void
  onCopy: () => void
  dataCy: string
  deleteDisabled?: boolean
  deleteLoading?: boolean
}) => {
  // Saving to the global library is an organisation write, unlike the rest of
  // these actions which only touch the project's own copy: offer it only to
  // an admin of the organisation, the same way the library manager does.
  const { isOrganisationAdmin } = useIsOrganisationAdmin()

  return (
    <Stack
      direction="row"
      spacing={0}
      alignItems="center"
      justifyContent="center"
      height="100%"
      width="100%"
    >
      <EditProjectAssemblyButton onEdit={onEdit} dataCy={dataCy} />
      <DownloadAssemblyButton onClick={onDownload} />
      {isOrganisationAdmin && (
        <>
          <Divider orientation="vertical" flexItem />
          <StoreAssemblyButton onClick={onStore} />
        </>
      )}
      <Divider orientation="vertical" flexItem />
      <DuplicateAssemblyButton onClick={onDuplicate} />
      <CopyAssemblyButton onClick={onCopy} dataCy={dataCy} />
      <Divider orientation="vertical" flexItem />
      <DeleteAssemblyButton onClick={onDelete} disabled={deleteDisabled} loading={deleteLoading} />
    </Stack>
  )
}

export {
  EditGlobalAssemblyButton,
  EditProjectAssemblyButton,
  DownloadAssemblyButton,
  StoreAssemblyButton,
  DuplicateAssemblyButton,
  DeleteAssemblyButton,
  CopyAssemblyButton,
  ProjectAssemblyActions,
}
